import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestfulApiService } from '../restful-api.service';

// ---- Shared enums / types ----

export type SettingDataType = 'STRING' | 'URL' | 'UUID' | 'BOOLEAN' | 'INTEGER' | 'DECIMAL' | 'EMAIL' | 'JSON' | 'ARRAY' | 'MAP';

// ---- Request DTOs ----

export interface PageSettingScalarDto {
  key: string;
  dataType: SettingDataType;
  value: string | null;
}

export interface LearnMoreItemPayload {
  uuid: string | null;
  title: string;
  description: string;
  imageUuid: string | null;
  sortOrder: number;
}

export interface CardPayload {
  uuid: string | null;
  header: string;
  description: string;
  visible: boolean;
  sortOrder: number;
}

export interface HomePageChampionSectionPayload {
  uuid: string | null;
  name: string;
  position: string;
  charityName: string;
  quote: string;
  imageUuid: string | null;
  logoUuid: string | null;
  visible: boolean;
  sortOrder: number;
}

export interface HomePageFaqItemPayload {
  uuid: string | null;
  question: string;
  answer: string;
  visible: boolean;
  sortOrder: number;
}

export type ContactPageHelpVideoSourceType = 'LINK_URL' | 'UPLOAD_FILE';

export interface ContactPageHelpVideoPayload {
  uuid: string | null;
  title: string;
  description: string;
  visible: boolean;
  sortOrder: number;
  videoSourceType: ContactPageHelpVideoSourceType;
  linkUrl: string | null;
  mediaUuid: string | null;
}

export interface AboutUsSectionPayload {
  uuid: string | null;
  title: string;
  description: string;
  closingParagraph: string | null;
  imageUuid: string | null;
  enableLearnMore: boolean;
  sortOrder: number;
  learnMoreItems: LearnMoreItemPayload[];
  cards: CardPayload[];
}

export interface BulkSavePageSettingPayload {
  category: string;
  settings: PageSettingScalarDto[];
  aboutUsSections?: AboutUsSectionPayload[];
  homePageChampions?: HomePageChampionSectionPayload[];
  homePageFaqItems?: HomePageFaqItemPayload[];
  contactPageHelpVideos?: ContactPageHelpVideoPayload[];
}

// ---- Response DTOs ----

export interface PageSettingDetailDto {
  key: string;
  dataType: SettingDataType;
  category: string;
  value: string | null;
  resolvedUrl: string | null;
}

export interface LearnMoreItemResponseDto {
  uuid: string;
  title: string | null;
  description: string | null;
  imageUuid: string | null;
  imageUrl: string | null;
  sortOrder: number;
  localizationTitle: string | null;
  localizationDescription: string | null;
}

export interface CardResponseDto {
  uuid: string;
  header: string | null;
  description: string | null;
  visible: boolean;
  sortOrder: number;
  localizationHeader: string | null;
  localizationDescription: string | null;
}

export interface AboutUsSectionResponseDto {
  uuid: string;
  title: string | null;
  description: string | null;
  closingParagraph: string | null;
  imageUuid: string | null;
  imageUrl: string | null;
  enableLearnMore: boolean;
  sortOrder: number;
  localizationTitle: string | null;
  localizationDescription: string | null;
  localizationClosingParagraph: string | null;
  learnMoreItems: LearnMoreItemResponseDto[];
  cards: CardResponseDto[];
}

export interface HomePageChampionSectionResponseDto {
  uuid: string;
  name: string | null;
  position: string | null;
  charityName: string | null;
  quote: string | null;
  localizationQuote: string | null;
  imageUuid: string | null;
  imageUrl: string | null;
  logoUuid: string | null;
  logoUrl: string | null;
  visible: boolean;
  sortOrder: number;
}

export interface HomePageFaqItemResponseDto {
  uuid: string;
  question: string | null;
  answer: string | null;
  localizationQuestion: string | null;
  localizationAnswer: string | null;
  visible: boolean;
  sortOrder: number;
}

export interface ContactPageHelpVideoResponseDto {
  uuid: string;
  title: string | null;
  description: string | null;
  visible: boolean;
  sortOrder: number;
  videoSourceType: ContactPageHelpVideoSourceType;
  linkUrl: string | null;
  mediaUuid: string | null;
  mediaUrl: string | null;
}

export interface PageSettingsResponseDto {
  settings: PageSettingDetailDto[];
  aboutPageSections: AboutUsSectionResponseDto[];
  homePageChampions: HomePageChampionSectionResponseDto[];
  homePageFaqItems: HomePageFaqItemResponseDto[];
  // Only returned for the Contact Us page category
  contactPageHelpVideos?: ContactPageHelpVideoResponseDto[];
}

export interface ComponentMediaUploadResponseDto {
  response: {
    uuid: string;
    url: string;
    filename: string;
    type: string;
    lastModified: number;
  };
}

@Injectable({ providedIn: 'root' })
export class PageSettingsService {
  constructor(private api: RestfulApiService) {}

  getAll(): Observable<{ response: PageSettingsResponseDto }> {
    return this.api.get('/v1/page-settings/all') as Observable<{ response: PageSettingsResponseDto }>;
  }

  getByCategory(category: string): Observable<{ response: PageSettingsResponseDto }> {
    return this.api.get(`/v1/page-settings/all/${category}`) as Observable<{ response: PageSettingsResponseDto }>;
  }

  bulkSave(payload: BulkSavePageSettingPayload): Observable<{ response: PageSettingsResponseDto }> {
    return this.api.post('/v1/page-settings/bulk-save', payload) as Observable<{ response: PageSettingsResponseDto }>;
  }

  uploadComponentMedia(file: File, componentType: string): Observable<ComponentMediaUploadResponseDto> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('componentType', componentType);
    return this.api.post('/v1/component-media/upload', formData) as Observable<ComponentMediaUploadResponseDto>;
  }

  deleteComponentMedia(fileUuid: string, componentType: string): Observable<{ response: string }> {
    return this.api.delete('/v1/component-media/delete', { fileUuid, componentType }) as Observable<{ response: string }>;
  }
}
